/**
 * Repository Layer - React Query hooks for user data
 * This layer manages data fetching, caching, and state
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  AdminUser,
  Department,
  UpdateUserPayload,
} from '@/types/user.types'
import { queryKeys } from '@/lib/query-client'
import {
  activateUser,
  confirmUserDepartment,
  deactivateUser,
  deleteUser,
  getAllUsers,
  getCurrentUser,
  getHubspotOwners,
  syncUserHubSpot,
  updateUser,
  updateUserDepartment,
} from '@/api/services/users.service'

/**
 * Hook to fetch user data by Keycloak ID
 * @param keycloakId - The Keycloak ID (sub claim from token)
 * @param enabled - Whether to enable the query (default: true if keycloakId is provided)
 */
/**
 * The signed-in user's identity, department and effective permissions.
 *
 * staleTime is deliberately short. This backs every route guard and nav
 * decision, and the global refetchOnWindowFocus only fires once a query is
 * stale — so the hour this replaced silently defeated it, and a revoked
 * permission could stay visible for that long. Two minutes plus a focus
 * refetch bounds it to about a tab switch. gcTime stays long so a backgrounded
 * tab still has something to render from.
 *
 * The client is never the boundary either way: the API resolves permissions
 * from the database on every request, so the worst a stale client does is show
 * a link that then 403s (sc-1215).
 */
export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.users.me(),
    queryFn: getCurrentUser,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 60,
  })
}

/**
 * Hook to fetch all users (admin view, returns full AdminUser type)
 */
export function useAllUsers() {
  return useQuery({
    queryKey: queryKeys.users.all,
    queryFn: getAllUsers,
  })
}

/**
 * Hook to update a user with optimistic updates
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string
      payload: UpdateUserPayload
    }) => updateUser(userId, payload),
    onSuccess: (updatedUser, { userId }) => {
      // Optimistic update: update all user list queries
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.all },
        (old: unknown) => {
          if (!old) return old
          if (Array.isArray(old)) {
            return old.map((user: AdminUser) =>
              user.id === userId ? updatedUser : user,
            )
          }
          return old
        },
      )
      toast.success('User updated successfully')
    },
    onError: (error: unknown) => {
      console.error('Failed to update user:', error)
      toast.error('Failed to update user')
    },
  })
}

/**
 * Hook to activate a user
 */
/**
 * Changes a user's department.
 *
 * Separate from `useUpdateUser` because department is an authorization change
 * and the API gates it on its own endpoint with an escalation floor, rather
 * than accepting it as a profile field (sc-1215).
 */
export function useUpdateUserDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      department,
    }: {
      userId: string
      department: Department
    }) => updateUserDepartment(userId, department),
    onSuccess: (updatedUser, { userId }) => {
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.all },
        (old: unknown) => {
          if (!old) return old
          if (Array.isArray(old)) {
            return old.map((user: AdminUser) =>
              user.id === userId ? updatedUser : user,
            )
          }
          return old
        },
      )
      toast.success('Department updated successfully')
    },
    onError: (error: unknown) => {
      console.error('Failed to update department:', error)
      toast.error('Failed to update department')
    },
  })
}

export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      toast.success('User activated successfully')
    },
    onError: (error: unknown) => {
      console.error('Failed to activate user:', error)
      toast.error('Failed to activate user')
    },
  })
}

/**
 * Hook to deactivate a user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, deleted }: { userId: string; deleted?: boolean }) =>
      deactivateUser(userId, deleted),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      toast.success('User deactivated successfully')
    },
    onError: (error: unknown) => {
      console.error('Failed to deactivate user:', error)
      toast.error('Failed to deactivate user')
    },
  })
}

/**
 * Hook to sync a user with HubSpot
 */
export function useSyncUserHubSpot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (email: string) => syncUserHubSpot(email),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      toast.success(`HubSpot sync complete for ${result.email}`)
    },
    onError: (error: unknown) => {
      console.error('Failed to sync user with HubSpot:', error)
      toast.error('Failed to sync user with HubSpot')
    },
  })
}

/**
 * Hook to delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      toast.success('User deleted successfully')
    },
    onError: (error: unknown) => {
      console.error('Failed to delete user:', error)
      toast.error('Failed to delete user')
    },
  })
}

/**
 * Fetch active users who have a hubspotOwnerId — used for owner dropdowns
 * when creating a HubSpot deal from the UI.
 */
export function useHubspotOwners() {
  return useQuery({
    queryKey: ['hubspot-owners'],
    queryFn: getHubspotOwners,
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

/**
 * Vouch for a user's access level without changing it.
 *
 * Also invalidates Learning: confirming is the moment their department-scoped
 * courses become assignable, so the onboarding panel that prompted this should
 * stop showing them as unclassified.
 */
export function useConfirmUserDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: confirmUserDepartment,
    onSuccess: () => {
      toast.success('Access level confirmed')
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.learning.all })
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not confirm it'),
  })
}
